// AuthService.js - Modified version
import 'whatwg-fetch'

import MemoryStorage from 'memorystorage'
import jwtDecode from 'jwt-decode'
import * as Sentry from '@sentry/react'

import { getUser, getCustomer } from '../actions/user'
import { connectionIssues } from '../actions/errors'
import { buildURL } from './api'

// AppStore
import AppStore from '../stores/AppStore'

// here, the MemoryStorage function is available
let myStorage

function isLocalStorageNameSupported () {
  try {
    const testKey = 'test'
    const storage = localStorage
    storage.setItem(testKey, '1')
    storage.removeItem(testKey)
    return true
  } catch (error) {
    return false
  }
}

if (!isLocalStorageNameSupported()) {
  console.log('Local storage not available')
  myStorage = new MemoryStorage('altrac-app')
} else {
  myStorage = localStorage
}

export default class AuthService {
  doAuthentication (authResult) {
    // Saves the user token
    this.setToken(authResult.idToken, authResult.idTokenPayload.exp)
    this.getUserInfo()
    return true
  }

  async login (email, password) {
    const url = buildURL('/users/login')

    try {
      const res = await fetch(url, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (res.status === 200) {
        connectionIssues(null, 'Connected')
        const json = await res.json()

        return new Promise((resolve, reject) => {
          this.setToken(json.token, jwtDecode(json.token).exp, (error) => {
            if (error) {
              reject(new Error('Issue setting token'))
              return
            }

            this.getUserInfo()
            resolve(true)
          })
        })
      } else {
        throw new Error('Login failed')
      }
    } catch (error) {
      console.error(error)
      if (error.toString() === 'Error: Login failed') {
        connectionIssues({ issue: 'Login failed', error })
      } else {
        connectionIssues({ issue: 'Login Server Request Failed', error })
      }
      throw error
    }
  }

  loggedIn () {
    // Checks if there is a saved token and it's still valid
    const token = this.getToken()
    return !!token.valid
  }

  checkEmail = (email) => {
    const url = buildURL('/users/exists', { email })
    return fetch(url)
      .then((res) => {
        connectionIssues(null, 'Connected')
        if (res.status === 200) {
          return res.json()
        }
        return null
      })
      .then((json) => json.exists)
      .catch((error) =>
        connectionIssues({ issue: 'Error fetching data', error })
      )
  }

  getUserInfo = () => {
    getUser(false, (error, result) => {
      if (error) {
        return console.error(error)
      }
      try {
        if (
          result &&
          result.data &&
          result.data.user &&
          result.data.user.customer_id
        ) {
          getCustomer(result.data.user.customer_id)
        } else {
          console.error('Missing user data in result:', result)
        }
      } catch (errorCust) {
        console.error('Could not get customer data', errorCust)
      }
      return true
    })
  }

  setToken (idToken, idTokenExpiry, callback) {
    // Saves user token to local storage
    if (idToken) {
      myStorage.setItem('idToken', idToken)
      if (!AppStore.auth) AppStore.auth = {}
      AppStore.auth.idToken = idToken
    }
    if (idTokenExpiry) {
      myStorage.setItem('idTokenExpiry', idTokenExpiry)
      if (!AppStore.auth) AppStore.auth = {}
      AppStore.auth.idTokenExpiry = idTokenExpiry
    }

    if (!AppStore.auth) AppStore.auth = {}
    AppStore.auth.loggedIn = this.loggedIn()
    AppStore.emitChange()

    if (callback) {
      callback(null, 'OK')
    }
  }

  getToken () {
    const token = {
      valid: false,
    }

    if (myStorage.getItem('idToken')) {
      token.idToken = myStorage.getItem('idToken')
    }

    if (myStorage.getItem('idTokenExpiry')) {
      token.idTokenExpiry = myStorage.getItem('idTokenExpiry')
    }

    if (myStorage.getItem('idToken') && myStorage.getItem('idTokenExpiry')) {
      if (new Date().getTime() / 1000 > myStorage.getItem('idTokenExpiry')) {
        token.valid = false
      } else {
        token.valid = true
      }

      if (!AppStore.auth) AppStore.auth = {}

      if (!AppStore.auth.idTokenExpiry || !AppStore.auth.idToken) {
        AppStore.auth.idToken = token.idToken
        AppStore.auth.idTokenExpiry = token.idTokenExpiry
        AppStore.emitChange()
        this.getUserInfo()
      }
    }

    if (!AppStore.auth) AppStore.auth = {}
    AppStore.auth.loggedIn = token.valid

    return token
  }

  logout () {
    // Clear user token and profile data from local storage
    myStorage.removeItem('idToken')
    myStorage.removeItem('idTokenExpiry')
    Sentry.setUser(null)

    if (!AppStore.auth) AppStore.auth = {}
    AppStore.auth.idToken = null
    AppStore.auth.idTokenExpiry = null
    AppStore.auth.loggedIn = false

    if (!AppStore.data) AppStore.data = {}
    AppStore.data.customer = {
      data: {},
      ready: false,
    }

    AppStore.emitChange()
  }

  createUser = ({ email, password, profile, address }, callback) => {
    const url = buildURL('/users/new')
    fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        profile,
        address,
      }),
    })
      .then((res) => {
        if (res.status === 201) {
          connectionIssues(null, 'Connected')
          return res.json()
        }
        throw new Error('User create failed')
      })
      .then((json) => {
        if (!json) {
          throw new Error('No response data')
        }
        if (callback) {
          callback(null, json)
        }
        return json
      })
      .catch((error) => {
        console.error('error', error)
        connectionIssues({ issue: error.toString() })
        if (callback) callback(error)
      })
  }

  passwordRecovery ({ email }) {
    this.checkEmail(email).then((exists) => {
      if (exists) {
        const url = buildURL('/users/passwordRecovery')
        fetch(url, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        })
        return { email }
      }

      return { error: 'Email address does not exist' }
    })
  }
}
